import { UserIcon } from 'lucide-react';
import { useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getTerritoryStatusLabel } from '@/constants/territories/status-display';
import { getTerritoryColor } from '@/lib/territories';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { Territory } from '@/types/territories/territory.type';

const MAX_VISIBLE = 15;

interface CandidatesTabProps {
  territory: Territory;
}

export const CandidatesTab = ({ territory }: CandidatesTabProps) => {
  const [showAll, setShowAll] = useState(false);
  const { zorTerritorySettings } = useZorTerritorySettingsStore();
  const assignedUser =
    territory.status == 'PENDING'
      ? territory.requested_by
      : territory.assigned_to;

  const territoryColor = getTerritoryColor(
    territory.status,
    zorTerritorySettings
  );

  const likers = territory.territory_likers || [];
  const hiddenCount = Math.max(0, likers.length - MAX_VISIBLE);
  const visibleLikes = showAll ? likers : likers.slice(0, MAX_VISIBLE);

  return (
    <div>
      <div className="flex gap-5 border-b border-border pb-4">
        <div className="w-60 space-y-1">
          <p className="as-title-04 text-muted-foreground">Status</p>
          <Badge color={territoryColor}>
            {getTerritoryStatusLabel(territory.status)}
          </Badge>
        </div>
        <div className="space-y-1">
          <p className="as-title-04 text-muted-foreground">Assign to</p>
          {assignedUser && territory.status != 'AVAILABLE' ? (
            <div className="flex items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarImage src={assignedUser.photo_url} />
                <AvatarFallback>
                  <UserIcon className="h-3 w-3" />
                </AvatarFallback>
              </Avatar>
              <p className="as-title-04 text-gray-700">
                {assignedUser.full_name}
              </p>
            </div>
          ) : (
            <p className="as-title-04 text-gray-700">N/A</p>
          )}
        </div>
      </div>
      <div className="space-y-2 pt-4">
        <p className="as-title-03 as-strong text-gray-700">
          {likers.length} {likers.length === 1 ? 'Liker' : 'Likers'}
        </p>
        <div className="flex flex-row flex-wrap gap-2">
          {visibleLikes.map((like) => (
            <div key={like.id} className="flex w-40 items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarImage src={like.photo_url} />
                <AvatarFallback>
                  <UserIcon className="h-3 w-3" />
                </AvatarFallback>
              </Avatar>
              <p className="as-title-04 as-strong truncate">{like.full_name}</p>
            </div>
          ))}
          {!showAll && hiddenCount > 0 && (
            <Button
              variant="ghost"
              className="px-0 text-primary hover:bg-transparent"
              onClick={() => setShowAll(true)}
            >
              +{hiddenCount} Show more
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
